<template>
  <div class="new-live-metrics" style="overflow: hidden">
    <div class="d-flex">
      <h2 class="page-label ml-3">
        {{ $t('message.pages.live.metrics.latencyTitle') }}
      </h2>

      <v-tooltip right>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon x-small class="ml-1 pb-1" v-bind="attrs" v-on="on">
            <v-icon style="color: var(--v-neutral-lighten1)" size="18">
              mdi-help-circle
            </v-icon>
          </v-btn>
        </template>
        <v-container
          class="text-body-2 pa-0"
          style="max-width: 250px"
          v-html="$t('message.pages.live.bufferingGraphDescription')"
        ></v-container>
      </v-tooltip>
    </div>
    <apexchart
      ref="apexChart"
      :class="{ darkThemeSvgFix: $vuetify.theme.dark }"
      type="area"
      height="250"
      :options="chartOptions"
      :series="series"
    />
    <div v-if="!isLoading" class="d-flex mt-4">
      <div class="d-flex flex-column mr-14 ml-5">
        <span class="values" style="color: var(--v-neutral-base)">
          {{ $t('message.pages.live.metrics.actualOccurrence') }}
        </span>
        <span
          v-if="liveStatus === 'offline' || liveStatus === 'finished'"
          class="actual-value"
        >
          -
        </span>
        <span v-else class="actual-value"> {{ totalYValue }} </span>
      </div>

      <div class="d-flex flex-column">
        <span class="values" style="color: var(--v-neutral-base)">{{
          $t('message.pages.live.metrics.averageOccurrence')
        }}</span>
        <span v-if="liveStatus === 'offline'" class="average-value"> - </span>
        <span v-else class="average-value"
          >{{ averageYValue.toFixed(2) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import liveService from '@/services/liveServiceV2'

export default {
  name: 'ChartComponent',
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    return {
      series: [],
      isDataLoaded: false,
      dataString: '',
      totalYValue: 0,
      isLoading: true,
      averageYValue: 0,
      liveStart: '00:00:00',
      actualOccurrence: 0,
      averageOccurrence: 0,
      liveStatus: 'offline',
      chartOptions: {
        chart: {
          type: 'area',
          stacked: false,
          zoom: { enabled: false },
          toolbar: { show: false },
          animations: {
            enabled: true,
            easing: 'linear',
            dynamicAnimation: {
              speed: 1000,
            },
          },
        },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.9,
          },
        },
        xaxis: {
          type: 'category',
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { show: false },
        },
        yaxis: {
          labels: {
            formatter: function (value) {
              if (!value) {
                value = 0
              }
              return value.toFixed(0)
            },
          },
        },
        tooltip: { shared: true, theme: 'dark' },
        legend: {
          position: 'top',
          horizontalAlign: 'right',
        },
      },
    }
  },
  async mounted() {
    this.$vuetify.theme.dark
      ? (this.chartOptions.tooltip.theme = 'dark')
      : (this.chartOptions.tooltip.theme = 'light')
    await this.getLive()
    this.getMetrics()
    this.startMetricsUpdate()
  },
  created() {
    this.$root.$on('statusChanged', (status) => {
      this.liveStatus = status
    })
  },
  beforeDestroy() {
    this.stopMetricsUpdate()
    this.$root.$on('statusChanged', null)
    this.$root.liveStatus = 'offline'
  },
  methods: {
    calculateTotalYValue() {
      let total = 0
      let count = 0

      this.series.forEach((serie) => {
        serie.data.forEach((point) => {
          total += point.y
          count += 1
        })
      })

      this.totalYValue = total
      this.averageYValue = count > 0 ? total / count : 0
    },
    updateChart(data, append = false) {
      const newSeries = Object.entries(data).map(([resolution, values]) => ({
        name: `${resolution}p`,
        data: Object.entries(values).map(([time, value]) => ({
          x: this.formatTime(new Date(time)),
          y: value,
        })),
      }))

      if (!append) {
        this.series = newSeries
      } else {
        newSeries.forEach((newSerie) => {
          let existingSerie = this.series.find((s) => s.name === newSerie.name)
          if (existingSerie) {
            newSerie.data.forEach((newDataPoint) => {
              if (
                !existingSerie.data.find(
                  (existingPoint) => existingPoint.x === newDataPoint.x
                )
              ) {
                existingSerie.data.push(newDataPoint)
              }
            })
          } else {
            this.series.push(newSerie)
          }
        })
      }
      if (
        this.$refs.apexChart &&
        typeof this.$refs.apexChart.updateSeries === 'function'
      ) {
        this.$refs.apexChart.updateSeries(this.series)
      }
    },

    async getMetrics() {
      const mockData = await liveService.getLatencyHistoric(
        this.$route.params.id
      )
      this.handleMetricsData(mockData.data)
      if (
        this.$refs.apexChart &&
        typeof this.$refs.apexChart.updateSeries === 'function'
      ) {
        this.$refs.apexChart.updateSeries(this.series)
      }
      this.isLoading = false
    },

    async getLastMetrics() {
      const mockData = await liveService.getLastLatencyHistoric(
        this.$route.params.id
      )
      this.handleMetricsData(mockData.data, true)
      this.calculateTotalYValue()
    },

    handleMetricsData(data, append = false) {
      this.updateChart(data, append)
    },

    startMetricsUpdate() {
      this.interval = setInterval(() => {
        if (this.liveStatus === 'online') {
          this.getLastMetrics()
        }
      }, 15000)
    },

    stopMetricsUpdate() {
      clearInterval(this.interval)
    },

    async getLive() {
      const response = await liveService.getLive(this.$route.params.id)
      this.liveStart = response.data.started_at
    },

    formatTime(date) {
      const isoStartDate = new Date(this.liveStart)
      let diff = date.getTime() - isoStartDate.getTime()
      let hours = Math.floor(diff / 3600000)
      let minutes = Math.floor((diff % 3600000) / 60000)
      const formattedHours = hours.toString().padStart(2, '0')
      const formattedMinutes = minutes.toString().padStart(2, '0')
      const seconds = '00'

      return `${formattedHours}:${formattedMinutes}:${seconds}`
    },
  },
}
</script>
<style lang="scss" scoped>
.actual-value {
  font-size: 20px;
  font-weight: 400;
  line-height: 24px;
  color: var(--v-brand-base);
}

.average-value {
  font-size: 20px;
  font-weight: 400;
  line-height: 24px;
  color: var(--v-neutral-lighten1);
}

.new-live-metrics {
  font-size: 0.875rem;
  color: var(--v-neutral-lighten1);
}

.bg-dark {
  .new-live-metrics {
    background-color: var(--v-neutral-darken2);
    color: white;
  }

  .values {
    color: var(--v-primary-base) !important;
  }

  .tspan {
    color: white !important;
  }

  .page-label {
    color: var(--v-primary-base) !important;
  }

  .page-title {
    color: var(--v-primary-base) !important;
  }
}
</style>
